import axios from 'axios';
import { API_URL } from '../config';

const getToken = () => {
  return localStorage.getItem('token') || '';
};

const mapPaymentFields = (paymentData) => {
  // Define your field name mappings
  const fieldMapping = {
    Biller: 'biller',
    'Payment Date': 'payment_date',
    'Payment Charge': 'payment_charge',
    'Payment Amount': 'payment_amount',
    'Payment Method': 'payment_method',
    Remarks: 'remarks',
    Source: 'source',
    Id: 'id',
    // Linked payments. The bill payments GET Title-Cases every key on the way
    // out, so a linked payment round-tripped through an edit would otherwise be
    // sent back with these names intact.
    Links: 'links',
    'Link Id': 'link_id',
    'Linked Type': 'linked_type',
    'Linked Parent Id': 'linked_parent_id',
    'Linked Payment Id': 'linked_payment_id',
    'Linked Parent Name': 'linked_parent_name',
    // Receipts/screenshots. Same story as Links above: the per-bill payments GET
    // Title-Cases the key, so an edit round-trip would otherwise POST the whole
    // attachment list back under a name the API does not know.
    Attachments: 'attachments'
  };

  // Create a new object with the renamed fields
  const mappedData = {};

  Object.keys(paymentData).forEach((key) => {
    // If the field has a mapping, use the mapped name; otherwise, use the original
    const mappedKey = fieldMapping[key] || key;
    mappedData[mappedKey] = paymentData[key];
  });

  return mappedData;
};

// FastAPI carries its error text in the JSON body's `detail`. axios callers read
// it off `err.response.data.detail`; the fetch-based calls in this file have to
// dig it out themselves, which is what this does.
const apiErrorFromResponse = async (response, fallback) => {
  let detail = '';
  try {
    const body = await response.json();
    const raw = body?.detail ?? body?.message;
    if (typeof raw === 'string') detail = raw;
  } catch {
    // No JSON body (or an empty one) — the fallback message stands.
  }

  const error = new Error(
    response.status === 409 ? 'Already linked to that one.' : detail || fallback
  );
  error.status = response.status;
  return error;
};

// --- Token refresh -------------------------------------------------------
// The access token lives 60 minutes. Rather than dumping the user at the login
// screen when it expires, we trade the refresh token for a new one.
//
// Two mechanisms, because they cover different gaps:
//   1. Proactive (ensureFreshToken): refresh just before expiry. This is what keeps
//      the raw fetch() calls in this file and in the Add pages working — they read
//      localStorage directly and never touch an axios interceptor.
//   2. Reactive (the 401 response interceptor): catch anything that slipped through.

const getRefreshToken = () => localStorage.getItem('refresh_token') || '';

const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('account_id');
  localStorage.removeItem('refresh_token');
};

const tokenExpiresAt = (jwt) => {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

// One refresh at a time. Six requests failing at once must trigger one refresh, not six.
let refreshInFlight = null;

const refreshAccessToken = async () => {
  const refresh_token = getRefreshToken();
  if (!refresh_token) return null;

  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_URL}/accounts/refresh`, { refresh_token })
      .then(({ data }) => {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('account_id', data.account_id);
        return data.access_token;
      })
      .catch((err) => {
        console.warn('Token refresh failed; session is over.', err);
        clearSession();
        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

// Refresh if the token expires within the next 2 minutes.
const ensureFreshToken = async () => {
  const token = getToken();
  if (!token) return '';
  const expiresAt = tokenExpiresAt(token);
  if (expiresAt && expiresAt - Date.now() < 2 * 60 * 1000) {
    const fresh = await refreshAccessToken();
    return fresh || '';
  }
  return token;
};

// The endpoints that are *about* getting a session rather than using one. A 401 from
// any of these is an answer to the caller ("wrong password", "that refresh token is
// dead"), not proof that an in-app session just expired, so neither interceptor may
// treat it as one. /accounts/signup is here because it is unauthenticated: dragging it
// through a token refresh used to bounce the Signup page to the landing page for anyone
// with a stale token still sitting in localStorage.
const AUTH_ENDPOINTS = [
  '/accounts/refresh',
  '/accounts/login',
  '/accounts/signup',
  '/accounts/google',
];

const isAuthUrl = (url = '') => AUTH_ENDPOINTS.some((path) => url.includes(path));

// Pages a logged-out visitor is entitled to sit on. They render their own errors, and
// '/' is where the redirect below would send them anyway, so there is nowhere to go.
const PUBLIC_ROUTES = ['/', '/login', '/signup'];

const onPublicRoute = () =>
  typeof window !== 'undefined' && PUBLIC_ROUTES.includes(window.location.pathname);

axios.interceptors.request.use(async (config) => {
  // Don't refresh on the way to an auth endpoint — /refresh would recurse, and the
  // others don't want the token in the first place.
  if (isAuthUrl(config.url)) {
    return config;
  }
  await ensureFreshToken();
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const isAuthCall = isAuthUrl(original.url);

    if (status === 401 && !original._retry && !isAuthCall && getRefreshToken()) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
        return axios(original); // replay the original request with the fresh token
      }
    }

    if (status === 401) {
      // Whatever we were holding is no good, so drop it either way.
      clearSession();

      // But only walk the user out of the app if they were actually in it. Bouncing
      // out of Login/Signup/the landing page just destroys whatever they were doing.
      if (!isAuthCall && !onPublicRoute()) {
        console.warn('401 Unauthorized - session over. Redirecting to login...');
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);


const apiService = {
  // PAYMENTS
  putPayment: async (paymentId, paymentData) => {
    const response = await fetch(`${API_URL}/bills/payments/${paymentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(paymentData),
    });
    const result = await response.json();
    return result;
  },

  getPayments: async (page, limit, period = null, startDate = '', endDate = '') => {
    try {
      let url = `${API_URL}/bills/payments?page=${page}&limit=${limit}`;

      if (period) {
        url += `&period=${period}`;
      } else if (startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      const payments = data.payments;
      const total_pages = data.total_pages;
      const returned_limit = data.limit;
      console.log(payments);
      return { items: payments, total_pages, limit: returned_limit };
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  },

  getPaymentsOneBillId: async (billId) => {
    try {
      const response = await fetch(`${API_URL}/bills/${billId}/payments`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      const payments = data.payments;
      console.log(payments);
      return { items: payments };
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  },

  updatePayment: async (billId, paymentId, updatedData) => {
    const renamedPaymentData = mapPaymentFields(updatedData);
    const response = await fetch(`${API_URL}/bills/${billId}/payments/${paymentId}`, {
      method: 'PUT', // or PATCH
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(renamedPaymentData),
    });
    if (!response.ok) {
      // console.log(JSON.stringify(renamedPaymentData))
      throw new Error('Failed to update payment');
    }
    return await response.json();
  },

  deletePayment: async (bill_id, payment_id) => {
    const response = await fetch(`${API_URL}/bills/${bill_id}/payments/${payment_id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response;
  },

  // The aggregate payments list carries a payment id and no bill id, so a delete
  // launched from it has to be payment-scoped rather than go through the
  // (bill_id, payment_id) route above. The server removes this parent's
  // reference and deletes the payment only once nothing else references it.
  //
  // This route is the one the backend is adding for the single-payment model.
  deletePaymentById: async (payment_id) => {
    const response = await fetch(`${API_URL}/bills/payments/${payment_id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to delete payment.');
    return response.json();
  },

  postPayment: async (bill_id, paymentData) => {
    const renamedPaymentData = mapPaymentFields(paymentData);

    const response = await fetch(`${API_URL}/bills/${bill_id}/payments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(renamedPaymentData),
    });
    return response;
  },

  getBillPayments: async (bill) => {
    try {
      const response = await fetch(`${API_URL}/bills/${bill._id}/payments`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      const payments = data.payments;
      return payments
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  },

  // LINKED PAYMENTS (bill side)
  // `type` is what the OTHER side is, so from a bill payment it is always
  // "subscription"; `id` is that subscription's _id. Each call ADDS one link —
  // a payment may be linked to several subscriptions; only re-linking the same
  // subscription is a 409.
  linkBillPayment: async (billId, paymentId, { type, id }) => {
    const response = await fetch(`${API_URL}/bills/${billId}/payments/${paymentId}/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ type, id }),
    });
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to link payment.');
    return response.json();
  },

  // `linkId` removes just that one link; omitting it removes ALL of the
  // payment's links.
  unlinkBillPayment: async (billId, paymentId, linkId) => {
    const base = `${API_URL}/bills/${billId}/payments/${paymentId}/link`;
    const response = await fetch(linkId ? `${base}/${encodeURIComponent(linkId)}` : base, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to unlink payment.');
    return response.json();
  },

  // PAYMENT ATTACHMENTS (receipts / screenshots)
  // Several files go up in one request: the field name is repeated, once per file,
  // which is what the server reads as a list. Note the deliberately missing
  // Content-Type header — setting it by hand drops the multipart boundary the
  // browser generates, and the server then cannot split the parts apart.
  // Both calls answer { message, attachments: [...the full list afterwards...] },
  // so the caller never has to re-fetch the payment to know where it stands.
  uploadPaymentAttachments: async (paymentId, files) => {
    const form = new FormData();
    Array.from(files || []).forEach((file) => form.append('attachments', file));

    const response = await fetch(`${API_URL}/bills/payments/${paymentId}/attachments`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: form,
    });
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to upload attachments.');
    return response.json();
  },

  deletePaymentAttachment: async (paymentId, attachmentId) => {
    const response = await fetch(
      `${API_URL}/bills/payments/${paymentId}/attachments/${encodeURIComponent(attachmentId)}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      }
    );
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to remove attachment.');
    return response.json();
  },

  // BILL PAYABLES (due cycles)
  // One payable = one due cycle = at most one payment. The API answers
  // { data: { items: [...computed...], summary: {...} } }; every item already
  // carries the server-computed status/days_diff/penalty, so nothing here
  // recomputes them. Errors arrive as FastAPI `detail` on err.response.data.
  getBillPayables: async (billId) => {
    const response = await axios.get(`${API_URL}/bills/${billId}/payables`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const data = response.data?.data || response.data || {};
    return {
      items: Array.isArray(data.items) ? data.items : [],
      summary: data.summary || {},
    };
  },

  addBillPayable: async (billId, data) => {
    const response = await axios.post(`${API_URL}/bills/${billId}/payables`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  },

  updateBillPayable: async (billId, payableId, data) => {
    const response = await axios.put(
      `${API_URL}/bills/${billId}/payables/${payableId}`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  },

  // 409 when the payable still has a linked payment — unlink it first.
  deleteBillPayable: async (billId, payableId) => {
    const response = await axios.delete(
      `${API_URL}/bills/${billId}/payables/${payableId}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return response.data;
  },

  // Appends a monthly series and skips any due_date already present, so
  // re-running it is safe. Answers { message, created, skipped }.
  generateBillPayables: async (billId, { start_date, months, amount = null }) => {
    const response = await axios.post(
      `${API_URL}/bills/${billId}/payables/generate`,
      { start_date, months: Number(months), amount },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  },

  // 409 when the payable already has a payment, or that payment already settles
  // another payable; 404 when the payment is not on this bill.
  linkPayablePayment: async (billId, payableId, paymentId) => {
    const response = await axios.post(
      `${API_URL}/bills/${billId}/payables/${payableId}/payment`,
      { payment_id: paymentId },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  },

  // Clears the link only — the payment record itself is untouched.
  unlinkPayablePayment: async (billId, payableId) => {
    const response = await axios.delete(
      `${API_URL}/bills/${billId}/payables/${payableId}/payment`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return response.data;
  },

  // BILLS
  getBillersOptions: async () => {
    const response = await axios.get(`${API_URL}/bills/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  },


  // PLANNERS
  exportPlannerPDF: async (id) => {
    try {
      const response = axios.get(`${API_URL}/planners/export_pdf/${id}`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      console.error("Error exporting planner PDF:", error);
      throw error;
    }
  },

  exportPlanner: async (id) => {
    try {
      const response = axios.get(`${API_URL}/planners/export_csv/${id}`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      console.error("Error exporting planner CSV:", error);
      throw error;
    }
  },

  deletePlanner: async (id) => {
    return axios.delete(`${API_URL}/planners/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  putPlanner: async (plannerData, plannerId) => {
    const response = await fetch(`${API_URL}/planners/${plannerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(plannerData),
    });
    const result = await response.json();
    return result;
  },

  postPlanners: async (plannerData) => {
    const response = await fetch(`${API_URL}/planners`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(plannerData),
    });
    return response;
  },

  getPlannersOptions: async () => {
    const response = await axios.get(`${API_URL}/planners/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response;
  },

  getPlanners: async (page, pageLimit) => {
    const response = await axios.get(`${API_URL}/planners`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: { "page": page, "limit": pageLimit },
    });
    return response.data;
  },

  getPlanner: async (plannerId) => {
    const response = await axios.get(`${API_URL}/planners/get_planner/${plannerId}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return response.data;
  },

  getDailyReports: async () => {
    const response = await axios.get(`${API_URL}/reports/dashboard`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  },

  putExpense: async (expenseId, payload) => {
    const response = await axios.put(`${API_URL}/expenses/${expenseId}`,
      payload,
      {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
    return response;
  },

  getExpensesOptions: async () => {
    const response = await axios.get(`${API_URL}/expenses/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.data;
  },

  // getExpensePlatformOptions: async () => {
  //   const response = await axios.get(`${API_URL}/expenses/get_options`, {
  //     headers: { Authorization: `Bearer ${getToken()}` },
  //   });
  //   return response.data.data.platforms;
  // },

  getExpenses: async (page, pageLimit) => {
    const response = await axios.get(`${API_URL}/expenses`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: { "page": page, "limit": pageLimit },
    });
    return response.data;
  },

  deleteExpense: async (id) => {
    return axios.delete(`${API_URL}/expenses/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  // The signup plan tiers (Free / Basic / Premium). /accounts/get_options is public —
  // it has to be, since the people who need it are by definition not logged in yet.
  getSubscriptionOptions: async () => {
    const response = await axios.get(`${API_URL}/accounts/get_options`);
    return response.data.options;
  },

  getCashFlowTypeOptions: async () => {
    const response = await axios.get(`${API_URL}/cash_flows/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.data.cash_flow_types;
  },

  getCashFlow: async (cash_flowId) => {
    const response = await axios.get(`${API_URL}/cash_flows/get_cash_flow/${cash_flowId}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return response.data;
  },

  putCashFlow: async (cash_flowId, payload) => {
    const response = await axios.put(`${API_URL}/cash_flows/${cash_flowId}`,
      payload,
      {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
    return response;
  },

  getBillerTypeOptions: async () => {
    const response = await axios.get(`${API_URL}/billers/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.data.biller_types;
  },

  getAmountTypeOptions: async () => {
    const response = await axios.get(`${API_URL}/billers/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    console.log("result coming from apiService: " + response.data.data.amount_types)
    return response.data.data.amount_types;
  },

  getBillers: async (page, pageLimit) => {
    try {
      const response = await axios.get(`${API_URL}/billers`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { page, limit: pageLimit },
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Optional: remove token or redirect to login
        console.warn('Unauthorized access – token may be invalid or expired.');
        throw new Error('Unauthorized'); // or return a custom object, e.g. { error: 'unauthorized' }
      }

      // Re-throw other errors
      throw error;
    }
  },

  getBills: async (page, pageLimit) => {
    const response = await axios.get(`${API_URL}/bills`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: { "page": page, "limit": pageLimit },
    });
    return response.data;
  },

  putBill: async (billId, billData) => {
    const response = await fetch(`${API_URL}/bills/${billId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(billData),
    });
    const result = await response.json();
    return result;
  },

  deleteBill: async (id) => {
    return axios.delete(`${API_URL}/bills/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  // SUBSCRIPTIONS (monthly payments - installments & subscriptions)
  getSubscriptions: async (page, pageLimit) => {
    const response = await axios.get(`${API_URL}/subscriptions`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: { page, limit: pageLimit },
    });
    return response.data;
  },

  postSubscription: async (data) => {
    const response = await axios.post(`${API_URL}/subscriptions`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  },

  putSubscription: async (id, data) => {
    const response = await axios.put(`${API_URL}/subscriptions/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  },

  deleteSubscription: async (id) => {
    return axios.delete(`${API_URL}/subscriptions/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  // The Subscriptions module's form options (billers, recurring types, statuses) — a
  // different thing entirely from the signup plan tiers above. This used to be a second
  // `getSubscriptionOptions`, and being later in the object literal it silently won,
  // so the signup dropdown was calling an authenticated endpoint as a logged-out
  // visitor and getting back a shape it couldn't read.
  getSubscriptionFormOptions: async () => {
    const response = await axios.get(`${API_URL}/subscriptions/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  },

  getSubscriptionPayments: async (subscriptionId) => {
    const response = await axios.get(`${API_URL}/subscriptions/${subscriptionId}/payments`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  },

  postSubscriptionPayment: async (subscriptionId, paymentData) => {
    const renamed = mapPaymentFields(paymentData);
    const response = await fetch(`${API_URL}/subscriptions/${subscriptionId}/payments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(renamed),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || data.message || 'Add payment failed');
    return data;
  },

  updateSubscriptionPayment: async (subscriptionId, paymentId, paymentData) => {
    const renamed = mapPaymentFields(paymentData);
    delete renamed.biller;
    const response = await fetch(
      `${API_URL}/subscriptions/${subscriptionId}/payments/${paymentId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(renamed),
      }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || data.message || 'Update payment failed');
    return data;
  },

  deleteSubscriptionPayment: async (subscriptionId, paymentId) => {
    const response = await fetch(
      `${API_URL}/subscriptions/${subscriptionId}/payments/${paymentId}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || data.message || 'Delete failed');
    return data;
  },

  // LINKED PAYMENTS (subscription side)
  // `type` is what the OTHER side is, so from a subscription payment it is
  // always "bill"; `id` is that bill's _id. Each call ADDS one link — a payment
  // may be linked to several bills; only re-linking the same bill is a 409.
  linkSubscriptionPayment: async (subscriptionId, paymentId, { type, id }) => {
    const response = await fetch(
      `${API_URL}/subscriptions/${subscriptionId}/payments/${paymentId}/link`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ type, id }),
      }
    );
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to link payment.');
    return response.json();
  },

  // `linkId` removes just that one link; omitting it removes ALL of the
  // payment's links.
  unlinkSubscriptionPayment: async (subscriptionId, paymentId, linkId) => {
    const base = `${API_URL}/subscriptions/${subscriptionId}/payments/${paymentId}/link`;
    const response = await fetch(linkId ? `${base}/${encodeURIComponent(linkId)}` : base, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to unlink payment.');
    return response.json();
  },

  // INVESTMENTS (manual holdings with contributions + value history)
  getInvestments: async (page, pageLimit) => {
    const response = await axios.get(`${API_URL}/investments/`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: { page, limit: pageLimit },
    });
    return response.data;
  },

  getInvestment: async (id) => {
    const response = await axios.get(`${API_URL}/investments/get_investment/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  },

  createInvestment: async (data) => {
    const response = await axios.post(`${API_URL}/investments/`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  },

  updateInvestment: async (id, data) => {
    const response = await axios.put(`${API_URL}/investments/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  },

  deleteInvestment: async (id) => {
    return axios.delete(`${API_URL}/investments/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  getInvestmentOptions: async () => {
    const response = await axios.get(`${API_URL}/investments/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.data.investment_types;
  },

  // Contributions (money-in records)
  getContributions: async (investmentId) => {
    const response = await axios.get(`${API_URL}/investments/${investmentId}/contributions`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.contributions || response.data.data?.contributions || [];
  },

  recordContribution: async (investmentId, data) => {
    const response = await axios.post(
      `${API_URL}/investments/${investmentId}/contributions`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  },

  deleteContribution: async (investmentId, contributionId) => {
    return axios.delete(
      `${API_URL}/investments/${investmentId}/contributions/${contributionId}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
  },

  // Value history (current-worth records)
  getValues: async (investmentId) => {
    const response = await axios.get(`${API_URL}/investments/${investmentId}/values`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.values || response.data.data?.values || [];
  },

  recordValue: async (investmentId, data) => {
    const response = await axios.post(
      `${API_URL}/investments/${investmentId}/values`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  },

  deleteValue: async (investmentId, valueId) => {
    return axios.delete(
      `${API_URL}/investments/${investmentId}/values/${valueId}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
  },

  // Portfolio-level summary (computed rollup + latest recorded snapshot)
  getInvestmentsSummary: async () => {
    const response = await axios.get(`${API_URL}/investments/summary`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.data?.summary || response.data.summary || null;
  },

  // Portfolio value snapshots (one aggregated value for the whole portfolio)
  getPortfolioValues: async () => {
    const response = await axios.get(`${API_URL}/investments/portfolio_values`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.data?.items || response.data.items || [];
  },

  createPortfolioValue: async (data) => {
    const response = await axios.post(`${API_URL}/investments/portfolio_values`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  },

  updatePortfolioValue: async (id, data) => {
    const response = await axios.put(
      `${API_URL}/investments/portfolio_values/${id}`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  },

  deletePortfolioValue: async (id) => {
    return axios.delete(`${API_URL}/investments/portfolio_values/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  // RECEIVABLES (money others owe me — parent + embedded payments received)
  getReceivables: async (page, pageLimit) => {
    const response = await axios.get(`${API_URL}/receivables/`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: { page, limit: pageLimit },
    });
    return response.data;
  },

  getReceivable: async (id) => {
    const response = await axios.get(`${API_URL}/receivables/get_receivable/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  },

  createReceivable: async (data) => {
    const response = await axios.post(`${API_URL}/receivables/`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  },

  updateReceivable: async (id, data) => {
    const response = await axios.put(`${API_URL}/receivables/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  },

  deleteReceivable: async (id) => {
    return axios.delete(`${API_URL}/receivables/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  getReceivableOptions: async () => {
    const response = await axios.get(`${API_URL}/receivables/get_options`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  },

  // Payments received (money-in records against a receivable)
  getReceivablePayments: async (receivableId) => {
    const response = await axios.get(`${API_URL}/receivables/${receivableId}/payments`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data.payments || response.data.data?.payments || [];
  },

  recordReceivablePayment: async (receivableId, data) => {
    const response = await axios.post(
      `${API_URL}/receivables/${receivableId}/payments`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  },

  deleteReceivablePayment: async (receivableId, paymentId) => {
    return axios.delete(
      `${API_URL}/receivables/${receivableId}/payments/${paymentId}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
  },

  deleteBiller: async (id) => {
    return axios.delete(`${API_URL}/billers/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  getCashFlows: async (page) => {
    const response = await axios.get(`${API_URL}/cash_flows`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: { page },
    });
    return response.data;
  },

  updateCashFlow: async (objectId) => {
    try {
      const response = await axios.put(`${API_URL}/cash_flows/${objectId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return response.data.item; // Return the data you need from the response
    } catch (error) {
      console.error('Error fetching cash flow:', error);
      throw error; // Re-throw or handle the error appropriately
    }
  },

  deleteCashFlow: async (id) => {
    return axios.delete(`${API_URL}/cash_flows/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
  },

  login: async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/accounts/login`, {
        username,
        password,
      });
      const { access_token, account_id } = response.data;

      // Store token and account_id in localStorage
      localStorage.setItem('token', access_token);
      localStorage.setItem('account_id', account_id);

      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { access_token, account_id };
    } catch (err) {
      console.error("Login failed:", err);
      throw new Error("Invalid login credentials");
    }
  },

  logout: async () => {
    try {
      const response = await axios.post(`${API_URL}/accounts/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      return response.data.message;
    } catch (err) {
      console.error("Logout failed:", err);
      throw new Error("Invalid credentials");
    }
  },

  // /accounts/signup is unauthenticated and deliberately carries no Authorization
  // header — sending a stale token dragged the request through a failing refresh and
  // threw the user off the page before the account was ever created.
  //
  // Returns the axios response on success and the error's response (400 with a string
  // `detail`, 422 with an array of `{loc, msg}`) on failure, so the page can render what
  // the server actually said. Returns null when there was no response at all.
  signup: async (userData) => {
    try {
      return await axios.post(`${API_URL}/accounts/signup`, userData);
    } catch (err) {
      console.error('Signup failed:', err);
      return err.response || null;
    }
  },

  // Exchange a Google ID token for an AdvPOS session. The backend verifies the token
  // against Google's keys and hands back the same JWT pair as a password login, so
  // nothing downstream needs to know how the user signed in.
  googleLogin: async (idToken) => {
    const response = await axios.post(`${API_URL}/accounts/google`, { id_token: idToken });
    return response.data;
  }
};

export { refreshAccessToken, ensureFreshToken, clearSession };
export default apiService;
